import React from 'react';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';
import { Grid, IconButton, Slider } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { SparkButton } from 'sharedComponents/UI';
import getCroppedImg from './utils';
const MAX_ZOOM = 3;
const MIN_ZOOM = 1;
const STEP = 0.1;
type EditorProps = {
  onClose: () => void;
  getCroppedImage?: (file: File | null | undefined, url: string) => void;
  imageSrc: string;
  aspectRatio?: number;
};
const Editor = ({
  onClose,
  imageSrc,
  getCroppedImage,
  aspectRatio = 1 / 1,
}: EditorProps) => {
  const [crop, setCrop] = React.useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(
    null
  );

  const onCropComplete = React.useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const isZoomMax = React.useMemo(() => zoom >= MAX_ZOOM, [zoom]);
  const isZoomMin = React.useMemo(() => zoom <= MIN_ZOOM, [zoom]);

  const handleZoomIncrease = React.useCallback(() => {
    if (!isZoomMax) setZoom((prevZoom) => prevZoom + STEP);
  }, [isZoomMax]);

  const handleZoomDecrease = React.useCallback(() => {
    if (!isZoomMin) setZoom((prevZoom) => prevZoom - STEP);
  }, [isZoomMin]);

  const handleSave = React.useCallback(async () => {
    const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
    if (croppedImage) {
      getCroppedImage &&
        getCroppedImage(
          croppedImage.croppedImageFile,
          croppedImage.croppedImageSrcURL
        );
    }
    onClose();
  }, [imageSrc, croppedAreaPixels, getCroppedImage, onClose]);

  const handleCancel = React.useCallback(() => {
    onClose();
    getCroppedImage && getCroppedImage(null, '');
  }, [onClose, getCroppedImage]);

  return (
    <Grid container position='relative' height='500px'>
      <Grid
        item
        position='absolute'
        bottom='100px'
        top={0}
        left={0}
        right={0}
        sx={{
          '& .reactEasyCrop_CropArea': { 
            ...(aspectRatio === 1 
              ? { 
                  width: '400px !important', 
                  height: '400px !important',
                  aspectRatio: '1 / 1 !important' 
                } 
              : { width: '550px !important' })
          },
        }}
      >
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          showGrid={false}
          aspect={aspectRatio}
          cropShape={aspectRatio === 1 ? 'rect' : 'rect'}
        />
      </Grid>
      <Grid
        item
        position='absolute'
        bottom='0'
        bgcolor='#FFF'
        right={0}
        left={0}
        container
        width='100%'
        display='flex'
        flexDirection='column'
      >
        <Grid item display='flex' alignItems='center' gap={1} width='100%'>
          <IconButton onClick={handleZoomDecrease} disabled={isZoomMin}>
            <Remove color={isZoomMin ? 'disabled' : 'primary'} />
          </IconButton>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            aria-labelledby='Zoom'
            onChange={(e, zoom) => setZoom(Number(zoom))}
            classes={{ root: 'slider' }}
          />
          <IconButton onClick={handleZoomIncrease} disabled={isZoomMax}>
            <Add color={isZoomMax ? 'disabled' : 'primary'} />
          </IconButton>
        </Grid>
        <Grid
          item
          display='flex'
          alignItems='center'
          gap={3}
          width='100%'
          justifyContent='flex-end'
          marginTop={1}
        >
          <SparkButton
            onClick={handleCancel}
            sx={{ width: '200px', padding: '10px 20px' }}
            variant='outlined'
          >
            Cancel
          </SparkButton>
          <SparkButton
            variant='contained'
            onClick={handleSave}
            sx={{ width: '200px', padding: '10px 20px' }}
          >
            Save
          </SparkButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Editor;
